import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Shield, ArrowLeft, Plus, Bug, Zap, Settings2 } from 'lucide-react';

const ChangelogPage = () => {
  const navigate = useNavigate();

  const releases = [
    {
      version: 'v1.10.0',
      date: 'January 15, 2025',
      changes: [
        { type: 'feature', icon: Plus, text: 'Screenshot monitoring system with automatic capture' },
        { type: 'feature', icon: Plus, text: 'Mobile responsive design across all pages' },
        { type: 'feature', icon: Plus, text: 'Settings page reorganization with categories' },
        { type: 'improvement', icon: Zap, text: 'Improved page load performance' },
        { type: 'fix', icon: Bug, text: 'Fixed PowerShell installer script formatting' }
      ]
    },
    {
      version: 'v1.9.0',
      date: 'December 20, 2024',
      changes: [
        { type: 'feature', icon: Plus, text: 'Enhanced hardware info collection (disk partitions, memory modules)' },
        { type: 'feature', icon: Plus, text: 'Asset health scoring system' },
        { type: 'feature', icon: Plus, text: 'Centralized agent version management' },
        { type: 'improvement', icon: Zap, text: 'Optimized Applications tab with caching' },
        { type: 'fix', icon: Bug, text: 'Fixed Terminal WebSocket connection' },
        { type: 'fix', icon: Bug, text: 'Resolved asset score "NA" display issue' }
      ]
    },
    {
      version: 'v1.8.0',
      date: 'November 30, 2024',
      changes: [
        { type: 'feature', icon: Plus, text: 'Client portal for ticket submission' },
        { type: 'feature', icon: Plus, text: 'Advanced search and filtering' },
        { type: 'improvement', icon: Zap, text: 'Enhanced security with MFA support' },
        { type: 'improvement', icon: Zap, text: 'Improved notification system' },
        { type: 'fix', icon: Bug, text: 'Fixed timezone handling in reports' }
      ]
    },
    {
      version: 'v1.7.0',
      date: 'November 1, 2024',
      changes: [
        { type: 'feature', icon: Plus, text: 'Real-time performance monitoring' },
        { type: 'feature', icon: Plus, text: 'Automated patch management' },
        { type: 'feature', icon: Plus, text: 'Custom report builder' },
        { type: 'improvement', icon: Zap, text: 'Database query optimization' },
        { type: 'fix', icon: Bug, text: 'Fixed memory leak in monitoring service' }
      ]
    },
    {
      version: 'v1.6.0',
      date: 'October 15, 2024',
      changes: [
        { type: 'feature', icon: Plus, text: 'ScreenConnect integration' },
        { type: 'feature', icon: Plus, text: 'Bulk actions for asset management' },
        { type: 'improvement', icon: Zap, text: 'Enhanced API documentation' },
        { type: 'fix', icon: Bug, text: 'Fixed date picker issues' }
      ]
    }
  ];

  const getTypeColor = (type) => {
    switch(type) {
      case 'feature': return 'text-green-600 bg-green-100';
      case 'improvement': return 'text-blue-600 bg-blue-100';
      case 'fix': return 'text-orange-600 bg-orange-100';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'feature': return 'New';
      case 'improvement': return 'Improved';
      case 'fix': return 'Fixed';
      default: return 'Changed';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/public/resources')} className="mr-2">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800">RMM Pro</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={() => navigate('/login')}>
                Login
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Changelog</h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Track all new features, improvements, and bug fixes
          </p>
        </div>
      </section>

      {/* Changelog */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {releases.map((release, index) => (
              <Card key={index} className="border-slate-200">
                <CardHeader className="border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-2xl">{release.version}</CardTitle>
                    <span className="text-slate-500">{release.date}</span>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-3">
                    {release.changes.map((change, changeIndex) => (
                      <li key={changeIndex} className="flex items-start">
                        <div className={`w-6 h-6 rounded flex items-center justify-center mr-3 flex-shrink-0 ${getTypeColor(change.type)}`}>
                          <change.icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <span className={`text-xs font-semibold mr-2 ${getTypeColor(change.type)} px-2 py-0.5 rounded`}>
                            {getTypeLabel(change.type)}
                          </span>
                          <span className="text-slate-700">{change.text}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Subscribe CTA */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Stay Updated
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Subscribe to get notified about new releases and updates
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
            <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
              Subscribe
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChangelogPage;